import { useEffect, useRef, useState, type Ref } from "react";
import { Paperclip, X, FileText } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import type { AttachmentNote } from "../types";

/** Shared note/comment editor. Files stay in the draft until its parent saves. */
export function MessageComposer({
  value,
  onChange,
  files,
  onFilesChange,
  placeholder,
  disabled = false,
  onBlur,
  id,
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  files: AttachmentNote[];
  onFilesChange: (files: AttachmentNote[]) => void;
  placeholder?: string;
  disabled?: boolean;
  onBlur?: () => void;
  id?: string;
  inputRef?: Ref<HTMLTextAreaElement>;
}) {
  const input = useRef<HTMLInputElement>(null),
    depth = useRef(0),
    owned = useRef(new Set<string>());
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    const active = new Set(files.map((file) => file.src));
    for (const url of owned.current)
      if (!active.has(url)) {
        URL.revokeObjectURL(url);
        owned.current.delete(url);
      }
  }, [files]);
  useEffect(() => {
    const urls = owned.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);
  const append = (items: File[]) => {
    if (disabled) return;
    const added = items.map((file) => {
      const src = URL.createObjectURL(file);
      owned.current.add(src);
      return { rawFile: file, src, title: file.name, type: file.type };
    });
    onFilesChange([...files, ...added]);
  };
  return (
    <div className="min-w-0 space-y-2">
      <div
        className="rovero-message-composer relative rounded-2xl border bg-background focus-within:ring-2 focus-within:ring-ring/40"
        onDragEnter={(e) => {
          if (!Array.from(e.dataTransfer.types).includes("Files") || disabled)
            return;
          e.preventDefault();
          depth.current++;
          setDragging(true);
        }}
        onDragOver={(e) => {
          if (Array.from(e.dataTransfer.types).includes("Files") && !disabled) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          if (--depth.current <= 0) {
            depth.current = 0;
            setDragging(false);
          }
        }}
        onDrop={(e) => {
          if (!Array.from(e.dataTransfer.types).includes("Files")) return;
          e.preventDefault();
          e.stopPropagation();
          depth.current = 0;
          setDragging(false);
          append(Array.from(e.dataTransfer.files));
        }}
      >
        <Textarea
          ref={inputRef}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          rows={4}
          className="min-h-28 max-h-[45dvh] rounded-2xl border-0 bg-transparent pb-12 shadow-none focus-visible:ring-0 resize-y"
        />
        <button
          type="button"
          data-mobile-control="icon"
          aria-label="Прикрепить файл"
          title="Прикрепить файл"
          disabled={disabled}
          onClick={() => input.current?.click()}
          className="absolute bottom-2 left-2 flex size-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
        >
          <Paperclip className="size-5" />
        </button>
        <input
          ref={input}
          type="file"
          multiple
          className="hidden"
          aria-label="Файлы сообщения"
          disabled={disabled}
          onChange={(e) => {
            append(Array.from(e.target.files || []));
            e.target.value = "";
          }}
        />
        {dragging && (
          <div
            className="pointer-events-none absolute inset-1 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-background/95 p-4 text-center text-sm font-medium"
            role="status"
          >
            Перетащите файлы сюда
          </div>
        )}
      </div>
      {files.length > 0 && (
        <ul
          className="flex min-w-0 flex-wrap gap-2"
          aria-label="Прикреплённые файлы"
        >
          {files.map((file, index) => (
            <li
              key={`${file.src}-${index}`}
              className="flex min-w-0 max-w-full items-center gap-2 rounded-xl border px-3 py-2 text-xs"
            >
              <FileText className="size-4 shrink-0" />
              <span className="min-w-0 break-all">{file.title}</span>
              <button
                data-mobile-control="compact"
                type="button"
                aria-label={`Убрать файл ${file.title}`}
                disabled={disabled}
                onClick={() => {
                  if (owned.current.delete(file.src))
                    URL.revokeObjectURL(file.src);
                  onFilesChange(files.filter((_, i) => i !== index));
                }}
                className="shrink-0 rounded-full p-1 hover:bg-muted"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
